import { Program, BN } from "@coral-xyz/anchor";
import {
  createRpc,
  deriveAddress,
  deriveAddressSeed,
  LightSystemProgram,
  defaultTestStateTreeAccounts,
  selectStateTreeInfo,
  accountCompressionProgram,
  noopProgram,
  getDefaultAddressTreeInfo,
  AddressWithTree,
  ValidityProofWithContext,
} from "@lightprotocol/stateless.js";
import { PublicKey, SystemProgram } from "@solana/web3.js";
import type { web3 } from "@coral-xyz/anchor";
import type { Dag } from "./dag";
import { RPC_ENDPOINT } from "~/constants";

// BN254 field prime (p)
const BN254_PRIME = new BN(
  "21888242871839275222246405745257275088548364400416034343698204186575808495617",
  10
);

// Type representing an address and its corresponding seed
export type AddressAndSeed = {
  address: PublicKey;
  seed: Uint8Array;
};

type LightInstructionData = {
  remainingAccounts: web3.AccountMeta[];
  outputMerkleTreeIndex: number;
  newAddresses: {
    addressMerkleTreePubkeyIndex: number;
    addressQueuePubkeyIndex: number;
    rootIndex: number;
  }[];
  proof: any;
};

export class LightClient {
  public commonAccounts: {
    lightSystemProgram: PublicKey;
    cpiSigner: PublicKey;
    registeredProgramPda: PublicKey;
    noopProgram: PublicKey;
    accountCompressionAuthority: PublicKey;
    accountCompressionProgram: PublicKey;
    selfProgram: PublicKey;
    systemProgram: PublicKey;
  };

  constructor(
    public rpc: ReturnType<typeof createRpc>,
    private program: Program<Dag>
  ) {
    this.commonAccounts = {
      lightSystemProgram: LightSystemProgram.programId,
      cpiSigner: PublicKey.findProgramAddressSync(
        [Buffer.from("cpi_authority")],
        this.program.programId
      )[0],
      registeredProgramPda: PublicKey.findProgramAddressSync(
        [LightSystemProgram.programId.toBuffer()],
        new PublicKey(accountCompressionProgram)
      )[0],
      noopProgram: new PublicKey(noopProgram),
      accountCompressionAuthority: PublicKey.findProgramAddressSync(
        [Buffer.from("cpi_authority")],
        LightSystemProgram.programId
      )[0],
      accountCompressionProgram: new PublicKey(accountCompressionProgram),
      selfProgram: this.program.programId,
      systemProgram: SystemProgram.programId,
    };
  }

  // Convert a buffer to a BN scalar and reduce it by the BN254 prime modulus
  public bufferToBN254Scalar(buffer: Uint8Array) {
    // Convert the buffer to a BN (big number)
    const bn = new BN(buffer);
    // Reduce the number modulo the BN254 prime to ensure it fits in the field
    return bn.mod(BN254_PRIME);
  }

  // Derives an address from the provided seeds and program ID
  public async deriveAddressWithSeed(seeds: Buffer[]): Promise<AddressAndSeed> {
    // Derive the seed from the provided seeds and program ID
    const addressSeed = deriveAddressSeed(seeds, this.program.programId);
    // Derive the address from the seed
    const address = await deriveAddress(addressSeed);
    // Return both the address and seed
    return { address, seed: addressSeed };
  }

  public get systemProgramMetas(): web3.AccountMeta[] {
    return Object.values(this.commonAccounts).map((pubkey) => ({
      isSigner: false,
      isWritable: false,
      pubkey,
    }));
  }

  private addOrGetInList(pubkey: string, list: string[]): number {
    const index = list.indexOf(pubkey);
    if (index !== -1) {
      return index;
    }
    list.push(pubkey);
    return list.length - 1;
  }

  /**
   * Get validity proofs for a list of addresses
   */
  public async getValidityProofs(
    addresses: PublicKey[],
    writableAccounts: string[] = []
  ): Promise<{
    proof: ValidityProofWithContext;
    addressQueue: PublicKey;
    addressTree: PublicKey;
    writableAccounts: string[];
    newAddresses: {
      addressMerkleTreePubkeyIndex: number;
      addressQueuePubkeyIndex: number;
      rootIndex: number;
    }[];
  }> {
    const { queue, tree } = getDefaultAddressTreeInfo();
    const newAddressesApi: AddressWithTree[] = [];
    addresses.forEach((address) => {
      newAddressesApi.push({
        address: this.bufferToBN254Scalar(address.toBuffer()),
        tree,
        queue,
      });
    });
    const proof = await this.rpc.getValidityProofV0(
      undefined,
      newAddressesApi
    );
    const newAddresses: {
      addressMerkleTreePubkeyIndex: number;
      addressQueuePubkeyIndex: number;
      rootIndex: number;
    }[] = [];
    proof.rootIndices.forEach((rootIndex, index) => {
      newAddresses.push({
        addressMerkleTreePubkeyIndex: this.addOrGetInList(
          proof.treeInfos[index].tree.toString(),
          writableAccounts
        ),
        addressQueuePubkeyIndex: this.addOrGetInList(
          proof.treeInfos[index].queue.toString(),
          writableAccounts
        ),
        rootIndex,
      });
    });
    return {
      proof,
      addressQueue: queue,
      addressTree: tree,
      writableAccounts,
      newAddresses,
    };
  }

  public async getStateTreeInfo(): Promise<{
    queue: PublicKey;
    tree: PublicKey;
  }> {
    if (this.rpc.rpcEndpoint.includes("helius")) {
      return selectStateTreeInfo(await this.rpc.getStateTreeInfos());
    }
    const { nullifierQueue: queue, merkleTree: tree } =
      defaultTestStateTreeAccounts();
    return { queue, tree };
  }

  // Get contexts including merkle and address-related data based on a leaf index
  public async getLightInstructionDataForNewAddress(
    addresses: PublicKey[]
  ): Promise<LightInstructionData> {
    const { queue: stateQueue, tree: stateTree } =
      await this.getStateTreeInfo();
    let writableAccounts: string[] = [stateTree.toString()];
    // Get default test state tree accounts
    const { proof, newAddresses } = await this.getValidityProofs(
      addresses,
      writableAccounts
    );

    // Return mapped remaining accounts along with merkle contexts
    return {
      remainingAccounts: [
        ...this.systemProgramMetas,
        ...writableAccounts.map((pubkey) => ({
          isSigner: false,
          isWritable: true,
          pubkey: new PublicKey(pubkey),
        })),
      ],
      outputMerkleTreeIndex: 0,
      newAddresses,
      proof: proof.compressedProof,
    };
  }
}

// Initialize Light Protocol RPC client
export function initializeLightClient(
  program: Program<Dag>,
  rpcUrl: string = RPC_ENDPOINT,
  compressionRpcUrl: string = RPC_ENDPOINT
) {
  try {
    console.log("Initializing Light Protocol client with RPC URL:", rpcUrl);
    console.log("Compression RPC URL:", compressionRpcUrl);
    
    // Create RPC client
    const rpc = createRpc(rpcUrl, compressionRpcUrl);
    
    // Create Light Protocol client instance
    return new LightClient(rpc, program);
  } catch (error) {
    console.error("Error initializing Light Protocol client:", error);
    throw new Error(`Failed to initialize Light Protocol client: ${error instanceof Error ? error.message : String(error)}`);
  }
} 